import {
    Box,
    Button,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Pagination,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Add, Edit, Delete } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { URLHandler } from "src/services/URLHandler";
import { Application, ApplicationParams } from "src/api/client-api/applications/useGetAllApplications";
import { useGetAllApplications } from "src/api/client-api/applications/useGetAllApplications";
import { useDeleteApplication } from "src/api/client-api/applications/useDeleteApplication";
import { useSnackbar } from "src/utils/useSnackbar";
import { formatDate } from "src/utils/formatting";
import { SecretKeyField } from "./components/SecretKeyField";

const ApplicationsTable = ({
    applications,
    onEdit,
    onDelete,
}: {
    applications: Application[];
    onEdit: (id: string) => void;
    onDelete: (id: string) => void;
}) => {
    const { t } = useTranslation();

    return (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>{t("applications.name", "Name")}</TableCell>
                        <TableCell>{t("applications.description", "Description")}</TableCell>
                        <TableCell>{t("applications.appId", "App ID")}</TableCell>
                        <TableCell>{t("applications.secretKey", "App Secret Key")}</TableCell>
                        <TableCell>{t("applications.createdAt", "Created At")}</TableCell>
                        <TableCell>{t("applications.actions", "Actions")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {applications.length === 0 ?
                        <TableRow>
                            <TableCell colSpan={6} align="center">
                                {t("applications.noApplications", "No applications found")}
                            </TableCell>
                        </TableRow>
                    :   applications.map((app) => (
                            <TableRow key={app.id}>
                                <TableCell>{app.name}</TableCell>
                                <TableCell>{app.description}</TableCell>
                                <TableCell>
                                    <SecretKeyField secretKey={app.id} />
                                </TableCell>
                                <TableCell>
                                    <SecretKeyField secretKey={app.secretKey} />
                                </TableCell>
                                <TableCell>{formatDate(app.createdAt)}</TableCell>
                                <TableCell sx={{ display: "flex", gap: 1 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<Edit />}
                                        onClick={() => onEdit(app.id)}
                                    >
                                        {t("applications.edit", "Edit")}
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        startIcon={<Delete />}
                                        onClick={() => onDelete(app.id)}
                                    >
                                        {t("applications.delete", "Delete")}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))
                    }
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export const ApplicationsList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { setSnackbarProps } = useSnackbar();

    // Pagination state
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    // Delete dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [applicationToDelete, setApplicationToDelete] = useState<string | null>(null);

    // Create params object for API
    const params: ApplicationParams = {
        page,
        limit,
    };

    // Use the applications hooks
    const { applications = [], totalItems = 0, isLoading, isError, error } = useGetAllApplications(params);
    const deleteApplicationMutation = useDeleteApplication();
    const isDeleting = deleteApplicationMutation.isLoading;

    // Reset to first page when status changes
    useEffect(() => {
        setPage(1);
    }, []);

    const handleCreateApplication = () => {
        navigate(URLHandler.settings.applications.create());
    };

    const handleEditApplication = (id: string) => {
        navigate(URLHandler.settings.applications.edit(id));
    };

    const handleDeleteClick = (id: string) => {
        setApplicationToDelete(id);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!applicationToDelete) return;

        try {
            await deleteApplicationMutation.mutateAsync({ id: applicationToDelete });

            setSnackbarProps({
                message: t("applications.deleteSuccess", "Application deleted successfully"),
                type: "success",
            });

            setDeleteDialogOpen(false);
            setApplicationToDelete(null);
        } catch (error) {
            console.error("Error deleting application:", error);

            setSnackbarProps({
                message: t("applications.deleteError", "Failed to delete application"),
                type: "error",
            });
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setApplicationToDelete(null);
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const handleLimitChange = (event: SelectChangeEvent<number>) => {
        setLimit(Number(event.target.value));
        setPage(1); // Reset to first page when changing limit
    };

    const totalPages = Math.ceil(totalItems / limit);

    return (
        <Box sx={{ padding: "2rem" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2rem" }}>
                <Typography variant="h4" component="h1">
                    {t("applications.title", "Applications")}
                </Typography>
                <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleCreateApplication}>
                    {t("applications.create", "Create Application")}
                </Button>
            </Box>

            {isLoading ?
                <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                    <CircularProgress />
                </Box>
            : isError ?
                <Box sx={{ my: 2, p: 2, bgcolor: "error.light", borderRadius: 1 }}>
                    <Typography color="error">
                        {t("applications.error", "Error")}:{" "}
                        {(error as Error)?.message || t("applications.unknownError", "An unknown error occurred")}
                    </Typography>
                </Box>
            :   <>
                    <ApplicationsTable
                        applications={applications}
                        onEdit={handleEditApplication}
                        onDelete={handleDeleteClick}
                    />

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mt: 3 }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography variant="body2">
                                    {t("applications.rowsPerPage", "Rows per page")}:
                                </Typography>
                                <FormControl size="small" sx={{ minWidth: 80 }}>
                                    <Select value={limit} onChange={handleLimitChange}>
                                        <MenuItem value={5}>5</MenuItem>
                                        <MenuItem value={10}>10</MenuItem>
                                        <MenuItem value={25}>25</MenuItem>
                                        <MenuItem value={50}>50</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={handlePageChange}
                                color="primary"
                                showFirstButton
                                showLastButton
                            />

                            <Typography variant="body2">
                                {t("applications.showing", "Showing")} {Math.min((page - 1) * limit + 1, totalItems)}-
                                {Math.min(page * limit, totalItems)} {t("applications.of", "of")} {totalItems}
                            </Typography>
                        </Stack>
                    )}
                </>
            }

            {/* Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{t("applications.confirmDelete", "Confirm Delete")}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {t(
                            "applications.deleteWarning",
                            "Are you sure you want to delete this application? This action cannot be undone.",
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} disabled={isDeleting}>
                        {t("applications.cancel", "Cancel")}
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        disabled={isDeleting}
                        startIcon={isDeleting ? <CircularProgress size={20} /> : <Delete />}
                        data-testid="delete-confirm-button"
                    >
                        {t("applications.delete", "Delete")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
